import type { LevelStreamer } from "../level/LevelStreamer";
import type { CameraBank } from "./CameraBank";

const BANK_RAD = 0.16;

// Reads the active segment's `bank` field each frame and tells the
// CameraBank to maintain a sustained roll while the player is inside
// it. CameraBank's own follow lerp handles the easing, so this is a
// straight pass-through.
//
// Behaviour:
//   bank === 1   -> camera rolls right (+BANK_RAD around Z)
//   bank === -1  -> camera rolls left  (-BANK_RAD around Z)
//   bank === undefined -> 0 (CameraBank lerps back to neutral)
//
// We deliberately don't smooth here: CameraBank.setSustainedRoll
// already feeds into a 5-lambda lerp (~0.4s settle time), so writing
// the target every frame produces a clean ease-in / ease-out without
// double-smoothing. clear() resets to neutral on state transitions.
export class BankingFx {
  private streamer: LevelStreamer;
  private cameraBank: CameraBank;

  constructor(streamer: LevelStreamer, cameraBank: CameraBank) {
    this.streamer = streamer;
    this.cameraBank = cameraBank;
  }

  update(): void {
    const seg = this.streamer.getSegmentAtPlayer();
    if (!seg) {
      this.cameraBank.setSustainedRoll(0);
      return;
    }
    if (seg.bank === 1) this.cameraBank.setSustainedRoll(BANK_RAD);
    else if (seg.bank === -1) this.cameraBank.setSustainedRoll(-BANK_RAD);
    else this.cameraBank.setSustainedRoll(0);
  }

  // Hard-reset the sustained roll. Called from MorphHexGame state
  // transitions (showMenu / startGame / advanceLevel / onDeath) so a
  // run never inherits a leftover bank from the previous segment.
  clear(): void {
    this.cameraBank.setSustainedRoll(0);
  }
}
